/**
 * SOK-37: horario del local y cierre puntual de un evento.
 *
 * sok-admin (SAD-63) guarda en `stores/{storeId}`:
 *
 *   schedule:   { monday: [12, 13, ..., 21], ... }   mismo formato que el de las categorías (ver
 *               schedule.js); ausente, vacío o mal formado = el local vende siempre.
 *   closing_at: Timestamp opcional. Desde ese instante el local queda cerrado ("Evento
 *               finalizado") sin importar el horario, hasta que alguien lo cambie o lo borre.
 *
 * Todo se evalúa en la zona horaria de la tienda y nunca en la del dispositivo: un tótem con la
 * zona mal configurada abriría o cerraría una hora corrido. Es la misma lógica que
 * `storeOpenStatus` de sok-admin (src/utils/storeSchedule.js).
 */

import { toSchedule } from "./schedule.js";

export const DEFAULT_TIME_ZONE = "America/Santiago";

// Minutos antes del cierre en que aparece la barra con la cuenta regresiva.
export const CLOSING_WARNING_MINUTES = 15;

export const CLOSED_REASONS = Object.freeze({
  EVENT: "event_closed",
  HOURS: "outside_hours",
});

const HOUR_MS = 60 * 60 * 1000;
// Un horario semanal se repite cada 168 horas: si no cambia en ese plazo, no cambia nunca.
const WEEK_HOURS = 7 * 24;

const WEEKDAY_BY_SHORT = { Sun: "sunday", Mon: "monday", Tue: "tuesday", Wed: "wednesday", Thu: "thursday", Fri: "friday", Sat: "saturday" };

const WEEKDAY_LABELS = {
  sunday: "domingo",
  monday: "lunes",
  tuesday: "martes",
  wednesday: "miércoles",
  thursday: "jueves",
  friday: "viernes",
  saturday: "sábado",
};

const isValidTimeZone = (tz) => {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Zona horaria de la tienda: `store.timezone` → `store.extra.timezone` → `client.timezone` →
 * America/Santiago, igual que `resolveTimeZone` de sok-admin.
 */
export function resolveTimeZone(store, client) {
  for (const tz of [store?.timezone, store?.extra?.timezone, client?.timezone]) {
    if (isValidTimeZone(tz)) return tz;
  }
  return DEFAULT_TIME_ZONE;
}

/** Instante (Date) del valor, o null. Acepta Timestamp de Firestore, Date, milisegundos o ISO. */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Día de la semana (llave en inglés), hora, minuto y segundo de `date` en la zona `tz`. */
export function zonedParts(date, tz) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    }).formatToParts(date).map((part) => [part.type, part.value]),
  );
  return { day: WEEKDAY_BY_SHORT[parts.weekday], hour: Number(parts.hour), minute: Number(parts.minute), second: Number(parts.second) };
}

const isOpenAt = (schedule, date, tz) => {
  const { day, hour } = zonedParts(date, tz);
  return (schedule[day] || []).includes(hour);
};

// Inicio de la hora en curso en la zona de la tienda. Se resta lo que va de la hora en vez de
// redondear el instante, porque hay zonas con desfase de media hora.
const startOfZonedHour = (date, tz) => {
  const { minute, second } = zonedParts(date, tz);
  return new Date(date.getTime() - minute * 60000 - second * 1000 - date.getMilliseconds());
};

// Primer inicio de hora después de `now` en que el horario pasa a `open`, o null si no pasa nunca.
const nextHourWhere = (schedule, now, tz, open) => {
  const start = startOfZonedHour(now, tz).getTime();
  for (let step = 1; step <= WEEK_HOURS; step += 1) {
    const candidate = new Date(start + step * HOUR_MS);
    if (isOpenAt(schedule, candidate, tz) === open) return candidate;
  }
  return null;
};

/**
 * Estado del local en un momento dado.
 *
 * @param {Object|null} store - Documento de la tienda (`schedule`, `closing_at`).
 * @param {Date} now - Momento a evaluar (la hora del servidor).
 * @param {string} tz - Zona horaria de la tienda (resolveTimeZone).
 * @returns {{open: boolean, reason: string|null, closesAt: Date|null, opensAt: Date|null}}
 *   `closesAt` es el próximo cierre si está abierto; `opensAt`, la próxima apertura según el
 *   horario si está cerrado. Un evento finalizado no tiene `opensAt`: solo vuelve a abrir cuando
 *   sok-admin borra o mueve `closing_at`.
 */
export function getStoreStatus(store, now, tz) {
  const closingAt = toDate(store?.closing_at);
  const schedule = toSchedule(store?.schedule);

  if (closingAt && now.getTime() >= closingAt.getTime()) {
    return { open: false, reason: CLOSED_REASONS.EVENT, closesAt: null, opensAt: null };
  }

  if (schedule && !isOpenAt(schedule, now, tz)) {
    const opensAt = nextHourWhere(schedule, now, tz, true);
    // Si el evento termina antes de la próxima apertura, ese día ya no se abre.
    const reopens = opensAt && (!closingAt || opensAt.getTime() < closingAt.getTime());
    return { open: false, reason: CLOSED_REASONS.HOURS, closesAt: null, opensAt: reopens ? opensAt : null };
  }

  const scheduleClose = schedule ? nextHourWhere(schedule, now, tz, false) : null;
  const closesAt = [scheduleClose, closingAt]
    .filter(Boolean)
    .reduce((earliest, date) => (!earliest || date.getTime() < earliest.getTime() ? date : earliest), null);
  return { open: true, reason: null, closesAt, opensAt: null };
}

/**
 * ¿Toca mostrar el aviso de cierre? Solo con el local abierto y a menos de `warningMinutes` del
 * cierre.
 */
export function isClosingSoon(status, now, warningMinutes = CLOSING_WARNING_MINUTES) {
  if (!status?.open || !status.closesAt) return false;
  const remaining = status.closesAt.getTime() - now.getTime();
  return remaining > 0 && remaining <= warningMinutes * 60000;
}

/** "21:00" en la zona de la tienda. */
export function formatStoreTime(date, tz) {
  return new Intl.DateTimeFormat("es-CL", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
}

const zonedDayKey = (date, tz) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

/** "hoy a las 12:00", "mañana a las 12:00" o "el lunes a las 12:00", en la zona de la tienda. */
export function formatOpening(opensAt, now, tz) {
  const time = formatStoreTime(opensAt, tz);
  const day = zonedDayKey(opensAt, tz);
  if (day === zonedDayKey(now, tz)) return `hoy a las ${time}`;
  if (day === zonedDayKey(new Date(now.getTime() + 24 * HOUR_MS), tz)) return `mañana a las ${time}`;
  return `el ${WEEKDAY_LABELS[zonedParts(opensAt, tz).day]} a las ${time}`;
}

/** Cuenta regresiva: "14:59", o "1:02:03" si queda más de una hora. Nunca negativa. */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
